import { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject, type ReactNode } from "react";
import { api, ApiError } from "../lib/api.js";
import type {
  AiContextItem,
  AiContextPreview,
  AiFinding,
  AiMode,
  AiStatus,
  Scene
} from "../lib/types.js";
import { Icon, type IconName } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";
import type { EditorControls } from "./Editor.js";
import { useModelPicker } from "../ai/ModelPicker.js";
import { AIModelSelect } from "../ai/AIModelSelect.js";
import { getChatModel, setChatModel } from "../ai/catalog.js";
import type { ModelRef, ModelTask } from "../lib/types.js";

/** Scribe mode → model-catalog task category (mirrors the backend mapping). */
const MODE_TASK: Record<AiMode, ModelTask> = {
  discuss: "general-chat",
  brainstorm: "brainstorm",
  critique: "critique",
  rewrite: "rewrite",
  continue: "rewrite",
  continuity: "continuity"
};

const MODES: { key: AiMode; label: string; icon: IconName; blurb: string }[] = [
  { key: "discuss", label: "Discuss", icon: "sparkles", blurb: "Talk through the story without changing anything." },
  { key: "brainstorm", label: "Brainstorm", icon: "sparkles", blurb: "Generate ideas, directions, conflicts, and beats." },
  { key: "critique", label: "Critique", icon: "search", blurb: "Analyse pacing, clarity, dialogue, consistency." },
  { key: "rewrite", label: "Rewrite", icon: "edit", blurb: "Rewrite selected text — you accept or reject." },
  { key: "continue", label: "Continue", icon: "chevronRight", blurb: "Suggest a continuation of the current scene." },
  { key: "continuity", label: "Continuity", icon: "alert", blurb: "Check against established story facts." }
];

interface ChatTurn {
  role: "user" | "assistant";
  content: string;
  contextUsed?: AiContextItem[];
}

interface Props {
  projectId: string;
  scene: Scene | null;
  selectedText: string;
  controlsRef: MutableRefObject<EditorControls | null>;
  onCollapse: () => void;
  onOpenScene: (sceneId: string) => void;
}

export function AiPanel({ projectId, scene, selectedText, controlsRef, onCollapse, onOpenScene }: Props) {
  const toast = useToast();
  const { pickModel } = useModelPicker();
  const [mode, setMode] = useState<AiMode>("discuss");
  const [status, setStatus] = useState<AiStatus | null>(null);
  // Persistent chat model (discuss/brainstorm) — no modal; remembered per project.
  const [chatModel, setChatModelState] = useState<ModelRef | null>(() => getChatModel(projectId));
  const [busy, setBusy] = useState(false);
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [contextPreview, setContextPreview] = useState<AiContextPreview | null>(null);
  const [contextOpen, setContextOpen] = useState(false);
  // Analyze the whole chapter (loads all its scenes) instead of just this scene.
  const [chapterScope, setChapterScope] = useState(false);

  // Conversational (discuss/brainstorm) state.
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [input, setInput] = useState("");

  // Single-shot mode results.
  const [analysis, setAnalysis] = useState<string | null>(null);
  const [findings, setFindings] = useState<AiFinding[] | null>(null);
  const [rewriteResult, setRewriteResult] = useState<{ original: string; suggestion: string } | null>(null);
  const [continuation, setContinuation] = useState<string | null>(null);
  const [instruction, setInstruction] = useState("");

  const active = MODES.find((m) => m.key === mode)!;
  const usesSelection = mode === "rewrite" || (mode === "critique");
  const selectionForContext = usesSelection ? selectedText : "";

  useEffect(() => {
    api.aiStatus(projectId).then(setStatus).catch(() => undefined);
  }, [projectId]);

  // Reset transient results when the mode or scene changes.
  useEffect(() => {
    setAnalysis(null);
    setFindings(null);
    setRewriteResult(null);
    setContinuation(null);
    setInstruction("");
  }, [mode, scene?.id]);

  // Load (cache) the saved conversation for the current conversational mode, so
  // chat survives reloads and mode switches.
  useEffect(() => {
    if (mode !== "discuss" && mode !== "brainstorm") return;
    let cancelled = false;
    (async () => {
      try {
        const { conversations } = await api.aiConversations(projectId);
        const latest = conversations.find((c) => c.mode === mode) ?? null;
        if (!latest) { if (!cancelled) { setTurns([]); setConversationId(null); } return; }
        const { messages } = await api.aiConversation(latest.id);
        if (cancelled) return;
        const hydrated: ChatTurn[] = messages
          .filter((m) => m.role === "user" || m.role === "assistant")
          .map((m) => {
            let contextUsed: AiContextItem[] | undefined;
            if (m.role === "assistant" && m.metadataJson) {
              try { contextUsed = (JSON.parse(m.metadataJson) as { contextUsed?: AiContextItem[] }).contextUsed; } catch { /* ignore */ }
            }
            return { role: m.role as "user" | "assistant", content: m.content, contextUsed };
          });
        setTurns(hydrated);
        setConversationId(latest.id);
      } catch { /* ignore — start fresh */ }
    })();
    return () => { cancelled = true; };
  }, [projectId, mode]);

  // Fetch the context preview (dynamic "Context Scribe will use").
  const excludedIds = useMemo(() => [...excluded], [excluded]);
  const refreshContext = useCallback(() => {
    api
      .aiContext(projectId, { mode, sceneId: scene?.id ?? null, selectedText: selectionForContext, excludedIds, chapterScope })
      .then(setContextPreview)
      .catch(() => setContextPreview(null));
  }, [projectId, mode, scene?.id, selectionForContext, excludedIds, chapterScope]);

  useEffect(() => {
    const t = window.setTimeout(refreshContext, 250);
    return () => window.clearTimeout(t);
  }, [refreshContext]);

  const call = useCallback(
    async (body: Parameters<typeof api.aiMessage>[1]) => {
      setBusy(true);
      try {
        return await api.aiMessage(projectId, { ...body, excludedIds });
      } catch (err) {
        if (err instanceof ApiError) {
          toast.error(err.message);
        } else {
          toast.error(err instanceof Error ? err.message : "Scribe request failed.");
        }
        return null;
      } finally {
        setBusy(false);
      }
    },
    [projectId, excludedIds, toast]
  );

  // ---- Actions ----
  const sendChat = async () => {
    const message = input.trim();
    if (!message) return;
    setInput("");
    setTurns((t) => [...t, { role: "user", content: message }]);
    const res = await call({ mode, conversationId, sceneId: scene?.id ?? null, message, chapterScope, provider: chatModel?.provider ?? null, model: chatModel?.model ?? null });
    if (res) {
      setConversationId(res.conversationId);
      setTurns((t) => [...t, { role: "assistant", content: res.reply, contextUsed: res.contextUsed }]);
    }
  };

  // Clear the saved chat (it persists as you go). Deletes the stored conversation
  // so it doesn't reload, then starts fresh.
  const clearConversation = async () => {
    if (conversationId) {
      try { await api.aiDeleteConversation(conversationId); } catch { /* ignore */ }
    }
    setTurns([]);
    setConversationId(null);
  };

  const runCritique = async () => {
    const choice = await pickModel({ task: MODE_TASK.critique });
    if (!choice) return;
    const target = chapterScope ? "chapter" : selectedText.trim() ? "selection" : "scene";
    const res = await call({
      mode: "critique",
      sceneId: scene?.id ?? null,
      selectedText: chapterScope ? null : selectedText.trim() || null,
      critiqueTarget: target,
      chapterScope,
      message: instruction.trim() || null,
      provider: choice.provider,
      model: choice.model
    });
    if (res) setAnalysis(res.reply);
  };

  const runContinuity = async () => {
    const choice = await pickModel({ task: MODE_TASK.continuity });
    if (!choice) return;
    const res = await call({ mode: "continuity", sceneId: scene?.id ?? null, message: instruction.trim() || null, provider: choice.provider, model: choice.model });
    if (res) setFindings(res.findings ?? []);
  };

  const runRewrite = async () => {
    const original = controlsRef.current?.getSelectedText() || selectedText;
    if (!original.trim()) {
      toast.error("Select some manuscript text to rewrite first.");
      return;
    }
    const choice = await pickModel({ task: MODE_TASK.rewrite });
    if (!choice) return;
    const res = await call({
      mode: "rewrite",
      sceneId: scene?.id ?? null,
      selectedText: original,
      message: instruction.trim() || null,
      provider: choice.provider,
      model: choice.model
    });
    if (res) setRewriteResult({ original, suggestion: res.reply });
  };

  const runContinue = async () => {
    const choice = await pickModel({ task: MODE_TASK.continue });
    if (!choice) return;
    const res = await call({ mode: "continue", sceneId: scene?.id ?? null, message: instruction.trim() || null, provider: choice.provider, model: choice.model });
    if (res) setContinuation(res.reply);
  };

  const acceptRewrite = () => {
    if (!rewriteResult) return;
    const ok = controlsRef.current?.replaceSelection(rewriteResult.suggestion);
    if (ok) {
      toast.success("Rewrite applied — saved to version history.");
      setRewriteResult(null);
    } else {
      toast.error("Couldn't find the original selection. Re-select the text and try again.");
    }
  };

  const insertContinuation = () => {
    if (!continuation) return;
    controlsRef.current?.insertText(continuation);
    toast.success("Continuation inserted — saved to version history.");
    setContinuation(null);
  };

  const copy = (text: string) => {
    navigator.clipboard?.writeText(text).then(
      () => toast.success("Copied."),
      () => toast.error("Copy failed.")
    );
  };

  const usingMock = status?.provider === "mock" || (!status?.hasRealProvider && (status?.provider ?? "mock") === "mock");

  return (
    <div className="ai-panel">
      <header className="ai-head">
        <div className="row gap-2">
          <span className="ai-badge"><Icon name="sparkles" size={15} /></span>
          <div className="col" style={{ lineHeight: 1.15 }}>
            <strong style={{ fontSize: 13 }}>Scribe AI</strong>
            <span
              className="ai-head-sub"
              title={status ? (status.provider ? `${status.provider}${status.model ? ` · ${status.model}` : ""}` : "not configured") : undefined}
            >
              {status ? (status.provider ?? "not configured") : "…"}
            </span>
          </div>
        </div>
        <button className="btn btn-icon btn-ghost btn-sm" title="Collapse AI panel" onClick={onCollapse}>
          <Icon name="panelRight" size={16} />
        </button>
      </header>

      <div className="ai-modes">
        {MODES.map((m) => (
          <button key={m.key} className={`ai-mode ${m.key === mode ? "active" : ""}`} onClick={() => setMode(m.key)} title={m.blurb}>
            <Icon name={m.icon} size={14} />
            {m.label}
          </button>
        ))}
      </div>

      {(mode === "discuss" || mode === "brainstorm") && (
        <div style={{ padding: "6px 12px 0" }}>
          <AIModelSelect
            projectId={projectId}
            value={chatModel}
            onChange={(ref) => { setChatModelState(ref); setChatModel(projectId, ref); }}
          />
        </div>
      )}

      {usingMock && (
        <div className="ai-mock-banner">
          <Icon name="info" size={13} />
          <span>Using the offline mock model. Set a provider &amp; key in the server env for real assistance.</span>
        </div>
      )}

      <ContextBar
        preview={contextPreview}
        open={contextOpen}
        onToggle={() => setContextOpen((o) => !o)}
        excludedCount={excluded.size}
        onExclude={(id) => setExcluded((s) => new Set(s).add(id))}
        onResetExcludes={() => setExcluded(new Set())}
      />

      <div className="ai-body">
        <div className="ai-mode-blurb">
          <Icon name={active.icon} size={15} style={{ color: "var(--accent)" }} />
          <span>{active.blurb}</span>
        </div>

        {(mode === "discuss" || mode === "critique") && scene && (
          <button
            className={`ai-scope-toggle${chapterScope ? " active" : ""}`}
            onClick={() => setChapterScope((v) => !v)}
            title="Analyze the whole chapter (loads every scene in it) instead of just this scene"
          >
            <Icon name={chapterScope ? "check" : "layers"} size={13} />
            Whole chapter
          </button>
        )}

        {(mode === "discuss" || mode === "brainstorm") && (
          <ChatView turns={turns} busy={busy} onClear={clearConversation} onOpenScene={onOpenScene} />
        )}

        {mode === "critique" && (
          <SingleShotView
            busy={busy}
            hasResult={analysis !== null}
            actionLabel={chapterScope ? "Critique chapter" : selectedText.trim() ? "Critique selection" : "Critique scene"}
            onRun={runCritique}
            instruction={instruction}
            setInstruction={setInstruction}
            placeholder="Optional: focus (e.g. dialogue, pacing)…"
            disabled={!scene}
          >
            {analysis !== null && (
              <div className="ai-result">
                <ProseBlock text={analysis} />
                <div className="row gap-2" style={{ marginTop: 10 }}>
                  <button className="btn btn-sm btn-ghost" onClick={() => copy(analysis)}><Icon name="file" size={13} /> Copy</button>
                  <button className="btn btn-sm btn-ghost" onClick={() => setAnalysis(null)}><Icon name="x" size={13} /> Clear</button>
                </div>
              </div>
            )}
          </SingleShotView>
        )}

        {mode === "rewrite" && (
          <div className="ai-mode-body">
            <div className="ai-selection-preview">
              <span className="label" style={{ margin: 0 }}>Selected text</span>
              {selectedText.trim() ? (
                <p className="ai-selected">{selectedText.trim().slice(0, 400)}{selectedText.trim().length > 400 ? "…" : ""}</p>
              ) : (
                <p className="faint" style={{ fontSize: 12 }}>Highlight text in the editor to rewrite it.</p>
              )}
            </div>
            <textarea className="textarea" rows={2} value={instruction} onChange={(e) => setInstruction(e.target.value)} placeholder="Optional instruction (e.g. tighten, more tension)…" />
            <button className="btn btn-primary" disabled={busy || !selectedText.trim()} onClick={runRewrite}>
              {busy ? <Icon name="clock" size={14} className="spin" /> : <Icon name="edit" size={14} />} Rewrite selection
            </button>
            {rewriteResult && (
              <div className="ai-diff">
                <div className="ai-diff-block">
                  <span className="ai-diff-label">Original</span>
                  <div className="ai-diff-text ai-diff-original">{rewriteResult.original}</div>
                </div>
                <div className="ai-diff-block">
                  <span className="ai-diff-label ai-diff-label-new">Suggested</span>
                  <div className="ai-diff-text ai-diff-suggested">{rewriteResult.suggestion}</div>
                </div>
                <div className="row gap-2">
                  <button className="btn btn-primary btn-sm" onClick={acceptRewrite}><Icon name="check" size={13} /> Accept</button>
                  <button className="btn btn-sm btn-ghost" onClick={() => setRewriteResult(null)}><Icon name="x" size={13} /> Reject</button>
                  <button className="btn btn-sm btn-ghost" onClick={() => copy(rewriteResult.suggestion)}><Icon name="file" size={13} /> Copy</button>
                  <button className="btn btn-sm btn-ghost" onClick={runRewrite}><Icon name="redo" size={13} /> Regenerate</button>
                </div>
              </div>
            )}
          </div>
        )}

        {mode === "continue" && (
          <div className="ai-mode-body">
            <textarea className="textarea" rows={2} value={instruction} onChange={(e) => setInstruction(e.target.value)} placeholder="Optional guidance for the continuation…" />
            <button className="btn btn-primary" disabled={busy || !scene} onClick={runContinue}>
              {busy ? <Icon name="clock" size={14} className="spin" /> : <Icon name="chevronRight" size={14} />} Generate continuation
            </button>
            {continuation && (
              <div className="ai-result">
                <ProseBlock text={continuation} />
                <div className="row gap-2" style={{ marginTop: 10, flexWrap: "wrap" }}>
                  <button className="btn btn-primary btn-sm" onClick={insertContinuation}><Icon name="plus" size={13} /> Insert</button>
                  <button className="btn btn-sm btn-ghost" onClick={() => copy(continuation)}><Icon name="file" size={13} /> Copy</button>
                  <button className="btn btn-sm btn-ghost" onClick={runContinue}><Icon name="redo" size={13} /> Regenerate</button>
                  <button className="btn btn-sm btn-ghost" onClick={() => setContinuation(null)}><Icon name="x" size={13} /> Discard</button>
                </div>
              </div>
            )}
          </div>
        )}

        {mode === "continuity" && (
          <div className="ai-mode-body">
            <textarea className="textarea" rows={2} value={instruction} onChange={(e) => setInstruction(e.target.value)} placeholder="Optional: what to focus on…" />
            <button className="btn btn-primary" disabled={busy || !scene} onClick={runContinuity}>
              {busy ? <Icon name="clock" size={14} className="spin" /> : <Icon name="alert" size={14} />} Check continuity
            </button>
            {findings !== null && (
              <div className="ai-findings">
                {findings.length === 0 ? (
                  <div className="ai-no-findings"><Icon name="check" size={16} /> No continuity issues found in this scene.</div>
                ) : (
                  findings.map((f, i) => <FindingCard key={i} finding={f} onOpenScene={onOpenScene} />)
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {(mode === "discuss" || mode === "brainstorm") && (
        <div className="ai-composer">
          <textarea
            className="textarea"
            rows={2}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void sendChat(); }
            }}
            placeholder={mode === "brainstorm" ? "Ask Scribe to brainstorm… (Enter to send, Shift+Enter for newline)" : "Ask about your story… (Enter to send, Shift+Enter for newline)"}
          />
          <button className="btn btn-primary" disabled={busy || !input.trim()} onClick={sendChat}>
            {busy ? <Icon name="clock" size={15} className="spin" /> : <Icon name="sparkles" size={15} />} Send
          </button>
        </div>
      )}
    </div>
  );
}

function ContextBar({
  preview,
  open,
  onToggle,
  excludedCount,
  onExclude,
  onResetExcludes
}: {
  preview: AiContextPreview | null;
  open: boolean;
  onToggle: () => void;
  excludedCount: number;
  onExclude: (id: string) => void;
  onResetExcludes: () => void;
}) {
  const count = preview?.items.length ?? 0;
  const grouped = useMemo(() => {
    const map = new Map<string, AiContextItem[]>();
    for (const item of preview?.items ?? []) {
      const list = map.get(item.group) ?? [];
      list.push(item);
      map.set(item.group, list);
    }
    return [...map.entries()];
  }, [preview]);

  return (
    <div className="ai-context-bar">
      <button className="ai-context-toggle" onClick={onToggle}>
        <Icon name={open ? "chevronDown" : "chevronRight"} size={13} />
        <Icon name="layers" size={13} style={{ color: "var(--text-muted)" }} />
        <span>Context Scribe will use</span>
        <span className="ai-context-count">{count}</span>
      </button>
      {open && (
        <div className="ai-context-detail">
          {count === 0 ? (
            <p className="faint" style={{ fontSize: 11.5, margin: "4px 8px" }}>No context assembled yet.</p>
          ) : (
            grouped.map(([group, items]) => (
              <div key={group} className="ai-context-group">
                <div className="ai-context-group-title">{group}</div>
                {items.map((item, i) => (
                  <div key={`${group}-${i}`} className="ai-context-item">
                    <Icon name="check" size={11} style={{ color: "var(--success)" }} />
                    <span className="truncate grow">{item.label}</span>
                    {item.ref && (
                      <button className="ai-context-x" title="Exclude from context" onClick={() => onExclude(item.ref!.id)}>
                        <Icon name="x" size={11} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ))
          )}
          {preview?.omitted && preview.omitted.length > 0 && (
            <p className="faint" style={{ fontSize: 10.5, margin: "6px 8px 0" }}>
              Trimmed to fit budget: {preview.omitted.map((o) => `${o.count} ${o.group}`).join(", ")}.
            </p>
          )}
          {excludedCount > 0 && (
            <button className="btn btn-sm btn-ghost" style={{ margin: "6px 8px" }} onClick={onResetExcludes}>
              <Icon name="undo" size={12} /> Re-include {excludedCount} excluded
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function ChatView({
  turns,
  busy,
  onClear,
  onOpenScene
}: {
  turns: ChatTurn[];
  busy: boolean;
  onClear: () => void;
  onOpenScene: (id: string) => void;
}) {
  const endRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [turns, busy]);

  return (
    <div className="ai-chat">
      <div className="ai-chat-bar">
        <span className="faint" style={{ fontSize: 11 }}>Chat is saved automatically.</span>
        <span className="spacer" />
        <button
          className="btn btn-sm btn-ghost"
          disabled={turns.length === 0}
          onClick={() => { if (turns.length === 0 || window.confirm("Clear this saved conversation? This can't be undone.")) void onClear(); }}
        >
          <Icon name="trash" size={13} /> Clear chat
        </button>
      </div>
      {turns.length === 0 ? (
        <div className="ai-chat-empty">
          <Icon name="sparkles" size={22} style={{ color: "var(--accent)" }} />
          <p className="muted">Ask Scribe anything about your story. It answers using the context above.</p>
        </div>
      ) : (
        turns.map((turn, i) => (
          <div key={i} className={`ai-turn ai-turn-${turn.role}`}>
            <ProseBlock text={turn.content} />
            {turn.role === "assistant" && turn.contextUsed && turn.contextUsed.length > 0 && (
              <div className="ai-turn-context">
                <div className="ai-ctx-scope">
                  <span className="faint">Context:</span>
                  {[...new Set(turn.contextUsed.map((c) => c.group))].map((g) => (
                    <span key={g} className="ai-ctx-chip"><Icon name="check" size={10} /> {g}</span>
                  ))}
                </div>
                {turn.contextUsed.some((c) => c.ref?.type === "scene") && (
                  <div className="ai-ctx-scenes">
                    {turn.contextUsed
                      .filter((c) => c.ref?.type === "scene")
                      .slice(0, 4)
                      .map((c) => (
                        <button key={c.ref!.id} className="ai-ctx-link" onClick={() => onOpenScene(c.ref!.id)}>{c.label}</button>
                      ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))
      )}
      {busy && <div className="ai-turn ai-turn-assistant"><span className="faint">Scribe is thinking…</span></div>}
      <div ref={endRef} />
    </div>
  );
}

function SingleShotView({
  busy,
  actionLabel,
  onRun,
  instruction,
  setInstruction,
  placeholder,
  disabled,
  children
}: {
  busy: boolean;
  hasResult: boolean;
  actionLabel: string;
  onRun: () => void;
  instruction: string;
  setInstruction: (v: string) => void;
  placeholder: string;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="ai-mode-body">
      <textarea className="textarea" rows={2} value={instruction} onChange={(e) => setInstruction(e.target.value)} placeholder={placeholder} />
      <button className="btn btn-primary" disabled={busy || disabled} onClick={onRun}>
        {busy ? <Icon name="clock" size={14} className="spin" /> : <Icon name="search" size={14} />} {actionLabel}
      </button>
      {children}
    </div>
  );
}

function FindingCard({ finding, onOpenScene }: { finding: AiFinding; onOpenScene: (id: string) => void }) {
  return (
    <div className={`finding-card finding-${finding.severity}`}>
      <div className="finding-head">
        <Icon name={finding.severity === "warning" ? "alert" : "info"} size={15} />
        <span className="finding-summary">{finding.summary}</span>
      </div>
      {finding.detail && <p className="finding-detail">{finding.detail}</p>}
      {finding.evidence.length > 0 && (
        <div className="finding-evidence">
          {finding.evidence.map((e, i) => (
            <div key={i} className="evidence-item">
              <div className="evidence-label">
                {e.label}
                {e.ref?.type === "scene" && (
                  <button className="ai-ctx-link" onClick={() => onOpenScene(e.ref!.id)}>open</button>
                )}
              </div>
              {e.quote && <div className="evidence-quote">“{e.quote}”</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Render model text with paragraph/line breaks preserved (no markdown dep). */
/** Inline markdown → React nodes: **bold**, *italic* / _italic_, `code`. Safe
 * (builds elements, never innerHTML). */
function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const re = /(\*\*([^*]+)\*\*)|(\*([^*]+)\*)|(_([^_]+)_)|(`([^`]+)`)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    if (m[2]) nodes.push(<strong key={`${keyPrefix}-${i}`}>{m[2]}</strong>);
    else if (m[4]) nodes.push(<em key={`${keyPrefix}-${i}`}>{m[4]}</em>);
    else if (m[6]) nodes.push(<em key={`${keyPrefix}-${i}`}>{m[6]}</em>);
    else if (m[8]) nodes.push(<code key={`${keyPrefix}-${i}`}>{m[8]}</code>);
    last = m.index + m[0].length;
    i++;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

/** Lightweight markdown block renderer for AI replies: headings, bullet/numbered
 * lists, and paragraphs with inline formatting. No external dependency. */
function ProseBlock({ text }: { text: string }) {
  const lines = text.replace(/\r\n/g, "\n").split("\n");
  const blocks: ReactNode[] = [];
  let para: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;
  let key = 0;

  const flushPara = () => {
    if (para.length === 0) return;
    const content = para;
    blocks.push(
      <p key={`p-${key++}`}>
        {content.map((l, j) => (
          <span key={j}>{renderInline(l, `p${key}-${j}`)}{j < content.length - 1 && <br />}</span>
        ))}
      </p>
    );
    para = [];
  };
  const flushList = () => {
    if (!list) return;
    const l = list;
    const Tag = l.ordered ? "ol" : "ul";
    blocks.push(
      <Tag key={`l-${key++}`} className="ai-prose-list">
        {l.items.map((it, j) => <li key={j}>{renderInline(it, `li${key}-${j}`)}</li>)}
      </Tag>
    );
    list = null;
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    const heading = /^(#{1,4})\s+(.*)$/.exec(line);
    const bullet = /^\s*[-*]\s+(.*)$/.exec(line);
    const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    if (heading) {
      flushPara(); flushList();
      const level = Math.min(heading[1]!.length + 1, 5); // h2..h5
      const Tag = `h${level}` as "h2" | "h3" | "h4" | "h5";
      blocks.push(<Tag key={`h-${key++}`} className="ai-prose-h">{renderInline(heading[2]!, `h${key}`)}</Tag>);
    } else if (bullet || numbered) {
      flushPara();
      const ordered = !!numbered;
      if (!list || list.ordered !== ordered) { flushList(); list = { ordered, items: [] }; }
      list.items.push((bullet ? bullet[1] : numbered![1])!);
    } else if (line.trim() === "") {
      flushPara(); flushList();
    } else {
      flushList();
      para.push(line);
    }
  }
  flushPara(); flushList();

  return <div className="ai-prose">{blocks}</div>;
}
